import { useEffect } from 'react';
import { themes, ThemeName } from './theme.config';

interface ThemeProviderProps {
  children: React.ReactNode;
  themeName?: ThemeName;
}

/**
 * ThemeProvider applies CSS custom properties to the root element
 * based on the selected theme.
 *
 * Theme preference is stored in localStorage and persists across sessions.
 */
export function ThemeProvider({ children, themeName = 'default' }: ThemeProviderProps) {
  const theme = themes[themeName];

  useEffect(() => {
    const root = document.documentElement;

    // Set CSS custom properties
    root.style.setProperty('--color-primary', theme.primary);
    root.style.setProperty('--color-primary-hover', theme.primaryHover);
    root.style.setProperty('--color-secondary', theme.secondary);
    root.style.setProperty('--color-accent', theme.accent);
    root.style.setProperty('--color-danger', theme.danger);
    root.style.setProperty('--color-success', theme.success);
    root.style.setProperty('--color-warning', theme.warning);
    root.style.setProperty('--color-info', theme.info);
    root.style.setProperty('--color-surface', theme.surface);
    root.style.setProperty('--color-surface-alt', theme.surfaceAlt);
    root.style.setProperty('--color-sidebar', theme.sidebar);
    root.style.setProperty('--color-sidebar-hover', theme.sidebarHover);
    root.style.setProperty('--color-sidebar-text', theme.sidebarText);
    root.style.setProperty('--color-text', theme.text);
    root.style.setProperty('--color-text-muted', theme.textMuted);
    root.style.setProperty('--color-border', theme.border);

    // Store preference
    localStorage.setItem('admin_theme', themeName);
  }, [theme, themeName]);

  return <>{children}</>;
}

/**
 * Get the stored theme name from localStorage
 */
export function getStoredTheme(): ThemeName {
  const stored = localStorage.getItem('admin_theme');
  if (stored && stored in themes) {
    return stored as ThemeName;
  }
  return 'default';
}
